

import React from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, TrendingDown, ChevronRight } from 'lucide-react';

interface StatCardWidgetProps {
  title: string;
  value: string | number;
  subtitle?: string;
  trend?: {
    value: number;
    isPositive: boolean;
  };
  icon?: React.ReactNode;
  color?: 'gold' | 'blue' | 'green' | 'red' | 'purple' | 'orange' | 'stone';
  /** Page the tile opens (the whole tile becomes a link) */
  to?: string;
  onClick?: () => void;
  loading?: boolean;
  /** Colour for the main number, e.g. red for a loss or an overdue amount */
  valueClassName?: string;
}

const colorClasses = {
  gold: 'bg-amber-50 border-amber-200 text-amber-900',
  blue: 'bg-blue-50 border-blue-200 text-blue-900',
  green: 'bg-green-50 border-green-200 text-green-900',
  red: 'bg-red-50 border-red-200 text-red-900',
  purple: 'bg-purple-50 border-purple-200 text-purple-900',
  orange: 'bg-orange-50 border-orange-200 text-orange-900',
  stone: 'bg-white border-stone-200 text-stone-900',
};

const iconColorClasses = {
  gold: 'text-amber-600',
  blue: 'text-blue-600',
  green: 'text-green-600',
  red: 'text-red-600',
  purple: 'text-purple-600',
  orange: 'text-orange-600',
  stone: 'text-stone-500',
};

export function StatCardWidget({
  title,
  value,
  subtitle,
  trend,
  icon,
  color = 'gold',
  to,
  onClick,
  loading = false,
  valueClassName = '',
}: StatCardWidgetProps) {
  const interactive = !!(to || onClick);
  const className = `group relative block text-left w-full min-h-[96px] rounded-xl border p-3.5 sm:p-5 transition-colors ${colorClasses[color]} ${
    interactive ? 'hover:border-amber-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400' : ''
  }`;

  const body = (
    <>
      <div className="flex items-start justify-between gap-2 mb-2">
        <p className="text-[11px] sm:text-xs font-bold uppercase tracking-wide opacity-70">{title}</p>
        {icon && <div className={`${iconColorClasses[color]} flex-shrink-0`}>{icon}</div>}
      </div>

      {loading ? (
        <div className="h-7 sm:h-9 bg-current opacity-10 rounded animate-pulse w-24 mb-1" />
      ) : (
        <p className={`text-lg sm:text-xl 2xl:text-2xl font-bold tracking-tight tabular-nums whitespace-nowrap ${valueClassName}`}>{value}</p>
      )}

      <div className={`flex items-center justify-between gap-2 mt-1 ${interactive ? 'pr-4' : ''}`}>
        {subtitle && !loading && <p className="text-xs opacity-70 leading-snug">{subtitle}</p>}

        {trend && (
          <div className={`flex items-center gap-1 text-xs sm:text-sm font-medium ${trend.isPositive ? 'text-green-600' : 'text-red-600'}`}>
            {trend.isPositive ? <TrendingUp className="w-3 h-3 sm:w-4 sm:h-4" /> : <TrendingDown className="w-3 h-3 sm:w-4 sm:h-4" />}
            <span>{Math.abs(trend.value)}%</span>
          </div>
        )}
      </div>

      {interactive && (
        <ChevronRight className="absolute bottom-3 right-3 w-4 h-4 opacity-30 group-hover:opacity-70 transition-opacity" aria-hidden="true" />
      )}
    </>
  );

  if (to) {
    return (
      <Link to={to} className={className}>
        {body}
      </Link>
    );
  }

  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={className}>
        {body}
      </button>
    );
  }

  return <div className={className}>{body}</div>;
}
